import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  BarChart3, TrendingUp, TrendingDown, Target, Zap, Trophy,
  Eye, MousePointer, ShoppingCart, Lightbulb, AlertCircle,
  ArrowUpRight, ArrowDownRight, RefreshCw, Sparkles, FlaskConical,
  Award, Activity, PieChart as PieChartIcon, Users, Calendar
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
  AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/campaign-creator`;

// Color palette for charts
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

// Metric Card Component
const MetricCard = ({ title, value, change, icon: Icon, trend, suffix = '' }) => {
  const isPositive = change >= 0;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;
  
  return (
    <Card data-testid={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className="text-3xl font-bold">
              {typeof value === 'number' ? value.toLocaleString() : value}
              {suffix && <span className="text-lg font-normal text-slate-500">{suffix}</span>}
            </p>
            {change !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                <TrendIcon className="w-4 h-4" />
                <span>{isPositive ? '+' : ''}{change}%</span>
                <span className="text-slate-400">vs last period</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${
            trend === 'up' ? 'bg-green-100 text-green-600' :
            trend === 'down' ? 'bg-red-100 text-red-600' :
            'bg-blue-100 text-blue-600'
          }`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Health Score Gauge
const HealthScoreGauge = ({ score }) => {
  const getColor = (score) => {
    if (score >= 80) return '#10B981';
    if (score >= 60) return '#F59E0B';
    return '#EF4444';
  };
  
  const getLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Needs Improvement';
    return 'Critical';
  };
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="64"
            cy="64"
            r="56"
            stroke="#E5E7EB"
            strokeWidth="12"
            fill="none"
          />
          <circle
            cx="64"
            cy="64"
            r="56"
            stroke={getColor(score)}
            strokeWidth="12"
            fill="none"
            strokeDasharray={`${score * 3.52} 352`}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold">{score}</span>
          <span className="text-xs text-slate-500">/ 100</span>
        </div>
      </div>
      <Badge 
        className="mt-2"
        style={{ backgroundColor: getColor(score), color: 'white' }}
      >
        {getLabel(score)}
      </Badge>
    </div>
  );
};

// Insight Card Component
const InsightCard = ({ insight }) => {
  const iconMap = {
    'trending-up': TrendingUp,
    'alert': AlertCircle,
    'lightbulb': Lightbulb
  };
  const Icon = iconMap[insight.icon] || Lightbulb;
  
  const priorityColors = {
    high: 'bg-red-50 border-red-200 text-red-800',
    medium: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    low: 'bg-green-50 border-green-200 text-green-800'
  };
  
  return (
    <div className={`p-4 rounded-lg border ${priorityColors[insight.priority] || priorityColors.medium}`}>
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 mt-0.5" />
        <div>
          <h4 className="font-medium">{insight.title}</h4>
          <p className="text-sm mt-1 opacity-80">{insight.description}</p>
        </div>
      </div>
    </div>
  );
};

// Main Component
const CampaignAnalytics = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('30');
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [topPerformers, setTopPerformers] = useState({ top_by_ctr: [], top_by_reach: [] });
  const [aiInsights, setAiInsights] = useState(null);
  const [providerComparison, setProviderComparison] = useState([]);
  
  // Progressive — chart tiles paint as they arrive. The AI insights call
  // can be slow (server-side LLM); previously it would freeze the whole
  // analytics page until it returned.
  const fetchAnalytics = (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    const tasks = [
      axios.get(`${API}/analytics/overview?token=${token}`)
        .then((r) => r.data.success && setOverview(r.data))
        .catch((e) => console.error('[CampaignAnalytics] overview failed:', e)),
      axios.get(`${API}/analytics/timeline?token=${token}&days=${timeRange}`)
        .then((r) => r.data.success && setTimeline(r.data.timeline || []))
        .catch((e) => console.error('[CampaignAnalytics] timeline failed:', e)),
      axios.get(`${API}/analytics/top-performers?token=${token}&limit=5`)
        .then((r) => r.data.success && setTopPerformers(r.data))
        .catch((e) => console.error('[CampaignAnalytics] top-performers failed:', e)),
      axios.get(`${API}/analytics/ai-insights?token=${token}`)
        .then((r) => r.data.success && setAiInsights(r.data.insights))
        .catch((e) => console.error('[CampaignAnalytics] ai-insights failed:', e)),
      axios.get(`${API}/analytics/provider-comparison?token=${token}`)
        .then((r) => r.data.success && setProviderComparison(r.data.provider_comparison || []))
        .catch((e) => console.error('[CampaignAnalytics] provider-comparison failed:', e)),
    ];
    let done = false;
    const off = () => {
      if (done) return;
      done = true;
      setLoading(false);
      setRefreshing(false);
    };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  };
  
  useEffect(() => {
    if (token) {
      fetchAnalytics();
    }
  }, [token, timeRange]);
  
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-accent mx-auto mb-4" />
          <p className="text-slate-500">Loading analytics...</p>
        </div>
      </div>
    );
  }
  
  const overviewData = overview?.overview || {};
  const platformData = overview?.platform_distribution || [];
  const typeData = overview?.campaign_type_distribution || [];
  
  return (
    <div className="space-y-6" data-testid="campaign-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-accent" />
            Campaign Analytics
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Track performance and get AI-powered insights
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={() => fetchAnalytics(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={() => navigate('/campaign-creator')}
            className="bg-accent hover:bg-accent/90"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Create Campaign
          </Button>
        </div>
      </div>
      
      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Impressions"
          value={overviewData.total_impressions || 0}
          icon={Eye}
          trend="up"
        />
        <MetricCard
          title="Total Clicks"
          value={overviewData.total_clicks || 0}
          icon={MousePointer}
          trend="up"
        />
        <MetricCard
          title="Average CTR"
          value={overviewData.avg_ctr || 0}
          suffix="%"
          icon={Target}
          trend={overviewData.avg_ctr > 2 ? 'up' : 'down'}
        />
        <MetricCard
          title="Conversions"
          value={overviewData.total_conversions || 0}
          icon={ShoppingCart}
          trend="up"
        />
      </div>
      
      {/* AI Insights & Health Score */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* AI Insights */}
        <Card className="lg:col-span-2" data-testid="ai-insights-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-accent" />
              AI-Powered Insights
            </CardTitle>
            <CardDescription>
              {aiInsights?.summary || 'Intelligent analysis of your campaign performance'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {aiInsights ? (
              <div className="space-y-4">
                {aiInsights.insights?.map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
                
                {aiInsights.recommendations?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-yellow-500" />
                      Recommendations
                    </h4>
                    <div className="space-y-2">
                      {aiInsights.recommendations.map((rec, idx) => (
                        <div 
                          key={idx}
                          className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                        >
                          <Badge 
                            variant="outline"
                            className={
                              rec.difficulty === 'easy' ? 'border-green-500 text-green-600' :
                              rec.difficulty === 'medium' ? 'border-yellow-500 text-yellow-600' :
                              'border-red-500 text-red-600'
                            }
                          >
                            {rec.difficulty}
                          </Badge>
                          <div>
                            <p className="font-medium text-sm">{rec.action}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              Expected: {rec.expected_impact}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Zap className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Run more A/B tests to unlock AI insights</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Health Score */}
        <Card data-testid="health-score-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-accent" />
              Campaign Health
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center py-4">
            <HealthScoreGauge score={aiInsights?.key_metrics?.health_score || 75} />
            
            <div className="mt-6 w-full space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Total Campaigns</span>
                <span className="font-medium">{overviewData.total_campaigns || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">A/B Tests</span>
                <span className="font-medium">{overviewData.total_tests || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Tests with Winners</span>
                <span className="font-medium text-green-600">{overviewData.tests_with_winners || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Running Tests</span>
                <span className="font-medium text-blue-600">{overviewData.running_tests || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Performance Timeline */}
      <Card data-testid="performance-timeline">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-accent" />
            Performance Timeline
          </CardTitle>
          <CardDescription>
            Track your metrics over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          {timeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={timeline}>
                <defs>
                  <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { timeZone: 'Asia/Dubai', month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="impressions" 
                  stroke="#3B82F6" 
                  fill="url(#colorImpressions)"
                  name="Impressions"
                />
                <Area 
                  type="monotone" 
                  dataKey="clicks" 
                  stroke="#10B981" 
                  fill="url(#colorClicks)"
                  name="Clicks"
                />
                <Line 
                  type="monotone" 
                  dataKey="ctr" 
                  stroke="#F59E0B" 
                  strokeWidth={2}
                  dot={{ fill: '#F59E0B' }}
                  name="CTR %"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No timeline data yet</p>
              <p className="text-sm">Start running A/B tests to see performance over time</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Bottom Row: Top Performers & Provider Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <Card data-testid="top-performers">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Top Performers
            </CardTitle>
            <CardDescription>
              Best performing content variants
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="ctr">
              <TabsList className="mb-4">
                <TabsTrigger value="ctr">By CTR</TabsTrigger>
                <TabsTrigger value="reach">By Reach</TabsTrigger>
              </TabsList>
              
              <TabsContent value="ctr">
                {topPerformers.top_by_ctr?.length > 0 ? (
                  <div className="space-y-3">
                    {topPerformers.top_by_ctr.map((item, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          idx === 0 ? 'bg-amber-100 text-amber-700' :
                          idx === 1 ? 'bg-slate-200 text-slate-700' :
                          idx === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.variant_name}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {item.test_name} • {item.provider}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-accent">{item.ctr}%</p>
                          <p className="text-xs text-slate-500">{item.clicks} clicks</p>
                        </div>
                        {item.is_winner && (
                          <Trophy className="w-4 h-4 text-amber-500" />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No performance data yet</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="reach">
                {topPerformers.top_by_reach?.length > 0 ? (
                  <div className="space-y-3">
                    {topPerformers.top_by_reach.map((item, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          idx === 0 ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.variant_name}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {item.test_name} • {item.platform}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-blue-600">{item.impressions.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">impressions</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Eye className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No reach data yet</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        {/* AI Provider Comparison */}
        <Card data-testid="provider-comparison">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-accent" />
              AI Provider Performance
            </CardTitle>
            <CardDescription>
              Compare results across different AI models
            </CardDescription>
          </CardHeader>
          <CardContent>
            {providerComparison.length > 0 ? (
              <div className="space-y-4">
                {providerComparison.map((provider, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{provider.provider}</span>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-500">{provider.variants_tested} tests</span>
                        <Badge className={provider.win_rate > 50 ? 'bg-green-500' : 'bg-slate-500'}>
                          {provider.win_rate}% wins
                        </Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 bg-blue-50 rounded">
                        <p className="text-lg font-bold text-blue-600">{provider.avg_ctr}%</p>
                        <p className="text-xs text-slate-500">Avg CTR</p>
                      </div>
                      <div className="p-2 bg-green-50 rounded">
                        <p className="text-lg font-bold text-green-600">{provider.total_clicks}</p>
                        <p className="text-xs text-slate-500">Clicks</p>
                      </div>
                      <div className="p-2 bg-purple-50 rounded">
                        <p className="text-lg font-bold text-purple-600">{provider.wins}</p>
                        <p className="text-xs text-slate-500">Wins</p>
                      </div>
                    </div>
                    {idx < providerComparison.length - 1 && <Separator className="mt-4" />}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <FlaskConical className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Test multiple AI providers to see comparisons</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Platform Distribution */}
        <Card data-testid="platform-distribution">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-accent" />
              Platform Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {platformData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={platformData}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {platformData.map((entry, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <PieChartIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No platform data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Campaign Type Distribution */}
        <Card data-testid="campaign-type-distribution">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-accent" />
              Campaign Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            {typeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={typeData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No campaign type data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CampaignAnalytics;
